// import { useState, useEffect } from 'react';
// import { toast } from 'react-hot-toast';

// /**
//  * A custom React hook to handle copying text to the clipboard.
//  * It manages the "copied" state and provides feedback.
//  *
//  * @returns {Array} A tuple containing:
//  *   - isCopied (boolean): True if the text was recently copied.
//  *   - copy (function): The function to call with the text to be copied.
//  */
// export function useCopyToClipboard() {
//   const [isCopied, setIsCopied] = useState(false);

//   // Use useEffect to reset the 'isCopied' state after a delay.
//   // This is cleaner than managing timeouts directly inside the copy function.
//   useEffect(() => {
//     let timeoutId;
//     if (isCopied) {
//       timeoutId = setTimeout(() => {
//         setIsCopied(false);
//       }, 2000); // Reset after 2 seconds
//     }
    
//     // Cleanup function to clear the timeout if the component unmounts
//     return () => {
//       clearTimeout(timeoutId);
//     };
//   }, [isCopied]); // This effect runs only when 'isCopied' changes

//   /**
//    * The function that performs the copy operation.
//    * @param {string} text - The plain text to copy to the clipboard.
//    */
//   const copy = async (text) => {
//     if (!navigator.clipboard) {
//       toast.error("Clipboard API not available in your browser.");
//       return;
//     }

//     try {
//       await navigator.clipboard.writeText(text);
//       setIsCopied(true); // This will trigger the useEffect to start the reset timer
//       toast.success("Copied to clipboard!");
//     } catch (error) {
//       console.error("Failed to copy text:", error);
//       toast.error("Could not copy text.");
//       setIsCopied(false); // Ensure state is false on error
//     }
//   };

//   // The hook returns the state and the function to trigger it.
//   return [isCopied, copy];
// }

import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

/**
 * A custom React hook to handle copying text to the clipboard with an 
 * automatic fallback mechanism for Office/Word Add-In desktop environments.
 */
export function useCopyToClipboard() {
 const [isCopied, setIsCopied] = useState(false);

 useEffect(() => {
  let timeoutId;
  if (isCopied) {
   timeoutId = setTimeout(() => {
    setIsCopied(false);
   }, 2000); // Reset after 2 seconds
  }
   
  return () => {
   clearTimeout(timeoutId);
  };
 }, [isCopied]);

 /**
  * Fallback copying mechanism for restricted iframe/desktop browser environments.
  */
 const fallbackCopy = (textToCopy) => {
  const textArea = document.createElement("textarea");
  textArea.value = textToCopy;
  
  // Position off-screen safely
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
   const successful = document.execCommand('copy');
   if (successful) {
    setIsCopied(true);
    toast.success("Copied to clipboard!");
   } else {
    toast.error("Failed to copy text.");
   }
  } catch (err) {
   console.error("Fallback copy execution failed:", err);
   toast.error("Could not copy text.");
  }
  
  document.body.removeChild(textArea);
 };

 /**
  * The main copy execution routing.
  */
 const copy = async (text) => {
  // If modern Clipboard API is missing, default immediately to fallback
  if (!navigator.clipboard || !navigator.clipboard.writeText) {
   fallbackCopy(text);
   return;
  }

  try {
   await navigator.clipboard.writeText(text);
   setIsCopied(true);
   toast.success("Copied to clipboard!");
  } catch (error) {
   // WebView2/iframes in Word block focus, so use fallback
   console.warn("Modern clipboard write failed, triggering fallback copy:", error);
   fallbackCopy(text);
  }
 };

 return [isCopied, copy];
}